/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import Row from "../../../common/core/Row";
import Col from "../../../common/core/Col";
import Input from "../../../common/core/Input";
import Button from "../../../common/core/Button";
import Dropdown from "../../../common/core/Dropdown";
import DropdownToggle from "../../../common/core/DropdownToggle";
import DropdownItem from "../../../common/core/DropdownItem";
import DropdownMenu from "../../../common/core/DropdownMenu";
import Tooltip from "../../../common/core/Tooltip";
import Constants from "../../../common/utils/Constants";
import Permission from "../../../common/security/Permission";
import Rules from "../../../common/security/PermissionRules";

const Search = (props) => {
  const {
    searchTerm,
    handleSearchTerm,
    handleSearch,
    handleKeyPress,
    handleSelectedCompany,
    companyId,
    companyIds,
  } = props;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const SearchTooltip = () => setTooltipOpen(!tooltipOpen);
  const { AVAILABLE_COMPANIES } = Constants;

  return (
    <Row>
      <Col md="8">
        <Permission
          allowed={Rules.Control.SEARCH_CUSTOMER}
        >
          <Row className="g-0 align-items-center">
            <Col className="pe-0 position-relative">
              <Input
                type="text"
                size="lg"
                value={searchTerm}
                onChange={handleSearchTerm}
                onKeyPress={(event) => handleKeyPress(event)}
                placeholder="Search Customer Account"
              />
              <i className="search-icon"> </i>
            </Col>
            <Col xs="auto" className="ps-0">
              <Button color="primary" onClick={handleSearch} className="btn-search" size="lg">
                Search
              </Button>
            </Col>
            <Col xs="auto" className="ms-2">
              <i className="fal fa-info-circle h5 text-danger" id="TooltipWildcard"> </i>
              <Tooltip
                placement="bottom"
                isOpen={tooltipOpen}
                target="TooltipWildcard"
                toggle={SearchTooltip}
                className="tooltip-lg bs-tooltip-bottom"
              >
                <p className="text-start mb-3">
                  You can do wildcard searches also:
                </p>
                <p className="text-start mb-3">
                  <Row className="g-2">
                    <Col xs="auto">
                      <i className="fal fa-share-all fw-bold"> </i>
                    </Col>
                    <Col>
                      423* will show all customers whose account number starts with 423
                    </Col>
                  </Row>
                </p>
                <p className="text-start mb-3">
                  <Row className="g-2">
                    <Col xs="auto">
                      <i className="fal fa-share-all fw-bold"> </i>
                    </Col>
                    <Col>
                      *423 will show all customers whose account number ends with 423
                    </Col>
                  </Row>
                </p>
                <p className="text-start mb-0">
                  <Row className="g-2">
                    <Col xs="auto">
                      <i className="fal fa-share-all fw-bold"> </i>
                    </Col>
                    <Col>
                      *423* will show all customers whose account number contains 423 anywhere
                    </Col>
                  </Row>
                </p>
              </Tooltip>
            </Col>
          </Row>
        </Permission>
      </Col>
      {companyId && (
        <>
          <Col md="4" className="mt-3 mt-md-0">
            <Permission
              allowed={Rules.Control.VIEW_CUSTOMERS_LIST}
            >
              <Dropdown isOpen={dropdownOpen && companyIds && companyIds.length > 1} toggle={toggle} className=" w-100 d-flex justify-content-end">
                <DropdownToggle caret color="white" className="dropdown-company" size="lg">{AVAILABLE_COMPANIES[companyId]}</DropdownToggle>
                {companyIds && companyIds.length > 1 && (
                  <DropdownMenu>
                    {companyIds.map((id) => (
                      <DropdownItem
                        active={id === companyId}
                        onClick={() => handleSelectedCompany(id)}
                      >
                        {AVAILABLE_COMPANIES[id]}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                )}
              </Dropdown>
            </Permission>
          </Col>
        </>
      )}
    </Row>
  );
};

Search.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  handleSearchTerm: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleKeyPress: PropTypes.func.isRequired,
  handleSelectedCompany: PropTypes.func.isRequired,
  companyId: PropTypes.string.isRequired,
  companyIds: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default Search;
